import {
  Box,
  Text,
  WizardStep,
  WizardStepRenderParams,
  useOnNext
} from '@factorialco/deprecated-design-system'
import { DatePickerValue, F0Alert, F0DatePicker, F0Select } from '@factorialco/f0-react'
import { NumberInput } from '@factorialco/f0-react/dist/experimental'
import { FinanceValueObjectsBudgetPeriodicityEnum } from 'generated/resources/graphql'
import moment from 'moment'

import i18n from 'modules/core/lib/i18n'
import { isNumber } from 'modules/core/lib/validateNumberInput'

import { periodicityOptions } from 'modules/finance/lib/budgets'
import { datePickerDateToString, stringDateToDatePicker } from 'modules/finance/lib/budgetDateUtils'
import {
  WizardData,
  WizardDataField,
  validateMandatoryFields
} from 'modules/finance/lib/newBudgetWizard'

interface ValidateProps {
  data: WizardData
  onDataChanged: (data: WizardData) => void
}
const validate = async ({ data, onDataChanged }: ValidateProps) => {
  const mandatoryFields: WizardDataField[] = ['effectiveFrom', 'periodicity', 'amountCents']

  const { invalidFields, dataWithErrors } = validateMandatoryFields(mandatoryFields, data)

  onDataChanged(dataWithErrors)

  if (invalidFields.length) {
    return Promise.reject()
  }

  if (!isNumber(data.amountCents) || Number(data.amountCents) <= 0) {
    onDataChanged({
      ...dataWithErrors,
      errors: {
        amountCents: i18n.t('budgets.forms.errors.invalid_amount')
      }
    })
    return Promise.reject()
  }

  if (data.effectiveUntil && moment(data.effectiveFrom).isAfter(moment(data.effectiveUntil))) {
    onDataChanged({
      ...dataWithErrors,
      errors: {
        effectiveFrom: i18n.t('budgets.forms.errors.start_date')
      }
    })
    return Promise.reject()
  }
}

const BudgetLimits = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  useOnNext(() => validate({ data, onDataChanged }))

  const handleStartDateChange = (
    value: DatePickerValue | undefined,
    _stringValue: string | undefined
  ) => {
    onDataChanged({ ...data, effectiveFrom: datePickerDateToString(value) ?? '' })
  }

  const handleEndDateChange = (
    value: DatePickerValue | undefined,
    _stringValue: string | undefined
  ) => {
    onDataChanged({ ...data, effectiveUntil: datePickerDateToString(value) })
  }

  // Convert periodicityOptions to F0Select format
  const periodicitySelectOptions = periodicityOptions().map((option) => ({
    value: option.value,
    label: option.label
  }))

  return (
    <Box gap="s40" flexDirection="column">
      <Box gap="s24" width="full">
        <Box gap="s8">
          <Text.Header2>{i18n.t('budgets.forms.new_budget.budget_limits.title')}</Text.Header2>

          <Text.Normal>{i18n.t('budgets.forms.new_budget.budget_limits.description')}</Text.Normal>
        </Box>
      </Box>
      <Box gap="s24" maxWidth="s600" flexDirection="column">
        <Box flexDirection="row" gap="s16">
          <Box style={{ flex: '1' }}>
            <F0DatePicker
              required
              label={i18n.t('budgets.forms.new_budget.budget_limits.start_date')}
              value={stringDateToDatePicker(data.effectiveFrom)}
              onChange={handleStartDateChange}
              placeholder={i18n.t(
                'budgets.forms.new_budget.budget_limits.start_date_placeholder'
              )}
              size="md"
              error={data.errors?.effectiveFrom}
            />
          </Box>
          <Box style={{ flex: '1' }}>
            <F0DatePicker
              label={i18n.t('budgets.forms.new_budget.budget_limits.end_date')}
              value={stringDateToDatePicker(data.effectiveUntil)}
              onChange={handleEndDateChange}
              placeholder={i18n.t('budgets.forms.new_budget.budget_limits.end_date_placeholder')}
              size="md"
              minDate={data.effectiveFrom ? moment(data.effectiveFrom).toDate() : undefined}
            />
          </Box>
        </Box>
        <Box flexDirection="row" gap="s16">
          <Box style={{ flex: '1' }}>
            <F0Select
              required
              label={i18n.t('budgets.forms.new_budget.budget_limits.time_frame')}
              options={periodicitySelectOptions}
              value={data.periodicity}
              placeholder={i18n.t('budgets.forms.new_budget.budget_limits.select_time_frame')}
              error={data.errors?.periodicity}
              onChange={(value: string) =>
                onDataChanged({
                  ...data,
                  periodicity: value as FinanceValueObjectsBudgetPeriodicityEnum
                })
              }
              size="md"
            />
          </Box>
          <Box style={{ flex: '1' }}>
            <NumberInput
              required
              label={i18n.t('budgets.forms.new_budget.budget_limits.amount_per_time_frame')}
              locale={i18n.locale}
              units={data.currency}
              value={data.amountCents ? Number(data.amountCents) : null}
              onChange={(value) =>
                onDataChanged({ ...data, amountCents: value !== null ? String(value) : '' })
              }
              error={data.errors?.amountCents}
              maxDecimals={2}
              size="md"
            />
          </Box>
        </Box>
        <F0Alert
          variant="info"
          title=""
          description={i18n.t('budgets.forms.new_budget.budget_limits.hint')}
        />
      </Box>
    </Box>
  )
}

export const budgetLimitsStep = (): WizardStep<WizardData> => ({
  title: i18n.t('budgets.forms.new_budget.budget_limits.title'),
  nextText: i18n.t('common.next'),
  render: (props) => <BudgetLimits {...props} />,
  isCompleted: () => true
})
